import axios from 'axios';
import { z } from 'zod';

import { env } from '@/config/env';
import { routePaths } from '@/routes/routeRegistry';
import {
  clearAdminSession,
  getAdminAccessToken,
  isAdminAuthenticated,
  syncAdminSessionFromStorage,
  useAdminAuthStore,
} from '@/stores/useAdminAuthStore';

const adminSessionClient = axios.create({
  ...(env.apiBaseUrl ? { baseURL: env.apiBaseUrl } : {}),
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

const refreshResponseSchema = z.object({
  data: z.object({
    status: z.literal('COMPLETED'),
    accessToken: z.string().min(1),
    tokenType: z.literal('Bearer'),
    expiresAt: z.iso.datetime({ offset: true }),
    loginId: z.string().min(1),
    displayName: z.string(),
    role: z.literal('ROLE_ADMIN'),
  }),
});

let refreshPromise: Promise<string> | null = null;
const REFRESH_BEFORE_EXPIRY_MS = 60_000;

const needsRefresh = () => {
  const expiresAt = Date.parse(useAdminAuthStore.getState().expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= Date.now() + REFRESH_BEFORE_EXPIRY_MS;
};

let sessionQueue: Promise<void> = Promise.resolve();

export const withAdminSessionLock = <T>(action: () => Promise<T>): Promise<T> => {
  const result = sessionQueue.then(async () => {
    // Serialize cookie changes in this tab and, where supported, across all tabs.
    if (typeof navigator !== 'undefined' && 'locks' in navigator) {
      return await navigator.locks.request('sonoschool-admin-session', action);
    }
    return action();
  });
  sessionQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
};

export const endAdminSession = () => {
  clearAdminSession();
  if (typeof window !== 'undefined' && window.location.pathname !== routePaths.adminLogin) {
    window.location.replace(routePaths.adminLogin);
  }
};

export const refreshAdminSession = (failedAccessToken?: string): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = withAdminSessionLock(async () => {
    syncAdminSessionFromStorage();
    const previous = useAdminAuthStore.getState();
    if (!isAdminAuthenticated()) throw new Error('관리자 로그인이 필요합니다.');

    // Another tab/request may already have renewed the failed token while we waited.
    if (!needsRefresh() && (!failedAccessToken || previous.accessToken !== failedAccessToken)) {
      return previous.accessToken;
    }

    const isCurrentSession = () => {
      const current = useAdminAuthStore.getState();
      return (
        current.isAuthenticated &&
        current.accessToken === previous.accessToken &&
        current.loginId === previous.loginId
      );
    };

    try {
      const response = await adminSessionClient.post<unknown>('/api/v1/auth/admin/refresh');
      const session = refreshResponseSchema.parse(response.data).data;
      if (!isCurrentSession()) throw new Error('관리자 세션이 변경되었습니다.');
      if (session.loginId !== previous.loginId) {
        endAdminSession();
        throw new Error('관리자 계정이 변경되었습니다. 다시 로그인해 주세요.');
      }
      useAdminAuthStore.getState().login({
        ...session,
        adminDisplayName: session.displayName,
      });
      return session.accessToken;
    } catch (error: unknown) {
      if (
        isCurrentSession() &&
        axios.isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 403)
      ) {
        endAdminSession();
      }
      // Network/server failures leave the form and session intact so the user can retry.
      throw error;
    }
  }).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};

export const ensureAdminAccessToken = async (): Promise<string> => {
  if (!isAdminAuthenticated()) return '';
  return needsRefresh() ? refreshAdminSession() : getAdminAccessToken();
};

export const logoutAdminSession = async (): Promise<void> => {
  clearAdminSession();
  // Queue immediately, so renewal, logout and a subsequent login cannot race cookie writes.
  await withAdminSessionLock(async () => {
    syncAdminSessionFromStorage();
    if (!isAdminAuthenticated()) {
      await adminSessionClient.post('/api/v1/auth/admin/logout');
    }
  });
};
